import { Button, Tooltip } from '@heroui/react'
import { requestDownload, useBulkToggleFavorite } from '@qse/api'
import { MediaType } from '@qse/api'
import type { ModalComponentProps } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { FileTypeIcon, VideoPlayer } from '@qse/ui'
import { Download, RotateCcw, Star, X, ZoomIn, ZoomOut } from 'lucide-react'
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  isCSVFile,
  isImageFile,
  isPdfFile,
  isSvgFile,
  isTextFile,
  isVideoFile,
  isWordFile,
  PreviewStatus,
} from '../../utils/file-preview-utils'
import CSVPreview from './csv-preview'
import PDFPreview from './pdf-preview'
import TextPreview from './text-preview'

interface FilePreviewData {
  id: string
  fileId: string
  name: string
  mediaType?: string
  status: PreviewStatus
  errorMessage?: string
  url?: string
  isFolder?: boolean
  // Optional (recommended): let API send these so we can fit even earlier.
  width?: number
  height?: number
  isFavorite?: boolean
  mimeType?: string
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

export default function FilePreviewModal(props: ModalComponentProps) {
  const { onClose } = props
  const { data, setData } = useActiveModelStore()
  const fileData = data as FilePreviewData

  const bulkToggleFavorite = useBulkToggleFavorite(
    fileData?.fileId ? [{ id: fileData.fileId, isFavorite: fileData.isFavorite }] : undefined,
  )
  const imgRef = useRef<HTMLImageElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // ===== Zoom/Pan state =====
  const [baseScale, setBaseScale] = useState(1)
  const [zoomFactor, setZoomFactor] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)

  // Start with CSS-fit so the image is visually fitted on the FIRST paint.
  const [useCssFit, setUseCssFit] = useState(true)

  // Smooth transform after first fit applied.
  const [animateTransforms, setAnimateTransforms] = useState(false)

  // ===== Image src strategy =====
  const [imgSrc, setImgSrc] = useState<string | null>(null)
  const [svgContent, setSvgContent] = useState<string | null>(null)
  const [svgLoading, setSvgLoading] = useState(false)
  const [svgError, setSvgError] = useState(false)
  // Blob fetch controller & URL cleanup
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const fetchCtrlRef = useRef<AbortController | null>(null)

  // Gesture helpers
  const dragStart = useRef<{ x: number; y: number } | null>(null)
  const pinchStartDist = useRef<number | null>(null)
  const pinchStartZoomFactor = useRef<number>(1)

  // Keep auto-fit state so we can re-fit on resize until the user interacts
  const autoFitRef = useRef<boolean>(false)

  // Track if user initiated download to auto-download when URL becomes available
  const [pendingDownload, setPendingDownload] = useState(false)
  const [isRequestingDownload, setIsRequestingDownload] = useState(false)

  const isProcessing = fileData?.status === 'PROCESSING' || fileData?.status === 'DECRYPTING'
  const hasError = fileData?.status === 'FAILED'
  const hasNoPreview = fileData?.status === 'NO_PREVIEW'
  const isAvailable = fileData?.status === 'AVAILABLE' && !!fileData?.url
  // Check if download is in progress (waiting for URL or requesting download)
  const isDownloading =
    isRequestingDownload ||
    pendingDownload ||
    (!fileData?.url && (fileData?.status === 'PROCESSING' || fileData?.status === 'DECRYPTING'))
  const isImage = isImageFile(fileData?.mediaType as MediaType)
  const isVideo = isVideoFile(fileData?.mediaType as MediaType)
  const isSvg = isSvgFile(fileData?.name)
  const isPdf = isPdfFile(fileData?.mediaType as MediaType)
  const isText = isTextFile(fileData?.mediaType as MediaType)
  const isWord = isWordFile(fileData?.mediaType as MediaType)
  const isCSV = isCSVFile(fileData?.mediaType as MediaType)

  // ===== Keyboard =====
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose()

        return
      }
      if (!isAvailable) return

      // Image, PDF, and text zoom controls
      if (isImage || isPdf || isText || isWord) {
        if (e.key === '+' || (e.key === '=' && (e.ctrlKey || e.metaKey))) {
          e.preventDefault()
          handleZoomIn()
        }
        if (e.key === '-' || (e.key === '_' && (e.ctrlKey || e.metaKey))) {
          e.preventDefault()
          handleZoomOut()
        }
        if (e.key === '0' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault()
          handleReset()
        }
      }
    }

    document.addEventListener('keydown', onKey)

    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailable, isImage, isPdf, isText, isWord])

  // ===== Compute baseScale (fit to container) - only for images =====
  const computeBaseScale = useCallback(() => {
    if (!isImage) return
    const img = imgRef.current
    const container = contentRef.current

    if (!img || !container) return

    const naturalW = img.naturalWidth || fileData?.width || 0
    const naturalH = img.naturalHeight || fileData?.height || 0

    if (!naturalW || !naturalH) return

    const rect = container.getBoundingClientRect()
    const scaleX = rect.width / naturalW
    const scaleY = rect.height / naturalH
    const fit = Math.min(scaleX, scaleY, 1) || 1

    setBaseScale(fit)
  }, [fileData?.width, fileData?.height, isImage])

  // ===== Mouse wheel zoom (only for images) =====
  useEffect(() => {
    if (!isImage) return
    const el = contentRef.current

    if (!el) return

    const onWheel = (e: WheelEvent) => {
      if (!isAvailable) return
      const direction = e.deltaY > 0 ? -1 : 1
      const factor = 0.1
      const next = clamp(zoomFactor + direction * factor, 0.1, 5)

      if (next !== zoomFactor) {
        e.preventDefault()
        autoFitRef.current = false
        if (useCssFit) {
          computeBaseScale()
          setUseCssFit(false)
          requestAnimationFrame(() => setAnimateTransforms(true))
        }
        setZoomFactor(next)
        if (next <= 1) setPan({ x: 0, y: 0 })
      }
    }

    el.addEventListener('wheel', onWheel, { passive: false })

    return () => el.removeEventListener('wheel', onWheel)
  }, [zoomFactor, isAvailable, useCssFit, isImage, computeBaseScale])

  // ===== Mouse pan (only for images) =====
  const onMouseDownImage = useCallback(
    (e: React.MouseEvent) => {
      if (!isImage || zoomFactor <= 1) return
      e.preventDefault()
      setIsDragging(true)
      dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }
      autoFitRef.current = false
    },
    [zoomFactor, pan, isImage],
  )

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isImage || !isDragging || !dragStart.current || zoomFactor <= 1) return
      setPan({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y })
    },
    [isDragging, zoomFactor, isImage],
  )

  const onMouseUp = useCallback(() => {
    setIsDragging(false)
    dragStart.current = null
  }, [])

  useEffect(() => {
    if (!isImage || !isDragging) return
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [isDragging, onMouseMove, onMouseUp, isImage])

  // ===== Touch (pinch + pan) - only for images =====
  useEffect(() => {
    if (!isImage) return
    const el = contentRef.current

    if (!el) return

    const getDist = (t1: Touch, t2: Touch) => {
      const dx = t1.clientX - t2.clientX
      const dy = t1.clientY - t2.clientY

      return Math.hypot(dx, dy)
    }

    const onTouchStart = (e: TouchEvent) => {
      if (!isAvailable) return
      if (e.touches.length === 2) {
        pinchStartDist.current = getDist(e.touches[0], e.touches[1])
        pinchStartZoomFactor.current = zoomFactor
      } else if (e.touches.length === 1 && zoomFactor > 1) {
        dragStart.current = { x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y }
        setIsDragging(true)
        autoFitRef.current = false
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!isAvailable) return
      if (e.touches.length === 2 && pinchStartDist.current) {
        e.preventDefault()
        autoFitRef.current = false
        const dist = getDist(e.touches[0], e.touches[1])
        const scale = dist / pinchStartDist.current
        const next = clamp(pinchStartZoomFactor.current * scale, 0.1, 5)

        if (useCssFit) {
          computeBaseScale()
          setUseCssFit(false)
          requestAnimationFrame(() => setAnimateTransforms(true))
        }
        setZoomFactor(next)
        if (next <= 1) setPan({ x: 0, y: 0 })
      } else if (e.touches.length === 1 && isDragging && dragStart.current && zoomFactor > 1) {
        e.preventDefault()
        setPan({
          x: e.touches[0].clientX - dragStart.current.x,
          y: e.touches[0].clientY - dragStart.current.y,
        })
      }
    }

    const onTouchEnd = () => {
      pinchStartDist.current = null
      setIsDragging(false)
      dragStart.current = null
    }

    el.addEventListener('touchstart', onTouchStart, { passive: false })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchEnd)

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [isAvailable, isDragging, zoomFactor, pan, useCssFit, isImage, computeBaseScale])

  const fitToContainer = useCallback(() => {
    if (!isImage) return
    computeBaseScale()
    setZoomFactor(1)
    setPan({ x: 0, y: 0 })
    autoFitRef.current = true
  }, [computeBaseScale, isImage])

  // ===== EARLY FIT: as soon as intrinsic size exists - only for images =====
  useEffect(() => {
    if (!isImage) return
    const img = imgRef.current

    if (!img || !imgSrc) return

    if ((fileData?.width && fileData?.height) || (img.naturalWidth > 0 && img.naturalHeight > 0)) {
      fitToContainer()
      setUseCssFit(false)
      requestAnimationFrame(() => setAnimateTransforms(true))

      return
    }

    let raf = 0
    const tryFit = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        fitToContainer()
        setUseCssFit(false)
        requestAnimationFrame(() => setAnimateTransforms(true))

        return
      }
      raf = requestAnimationFrame(tryFit)
    }

    raf = requestAnimationFrame(tryFit)

    return () => cancelAnimationFrame(raf)
  }, [imgSrc, fitToContainer, fileData?.width, fileData?.height, isImage])

  // ===== Window resize (keep auto-fit until user interacts) - only for images =====
  useEffect(() => {
    if (!isImage) return
    const onResize = () => {
      const prevAuto = autoFitRef.current

      computeBaseScale()
      if (prevAuto) {
        setZoomFactor(1)
        setPan({ x: 0, y: 0 })
      }
    }

    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [computeBaseScale, isImage])

  // ===== Image load (do NOT await decode) - only for images =====
  const onImageLoad = useCallback(() => {
    if (!isImage) return
    requestAnimationFrame(() => setAnimateTransforms(true))
  }, [isImage])

  useEffect(() => {
    if (isSvg && imgSrc) {
      const fetchSvg = async () => {
        try {
          setSvgLoading(true)
          const response = await fetch(imgSrc)
          const svgText = await response.text()

          setSvgContent(svgText) // Set the fetched SVG content
        } catch (error) {
          setSvgError(true)
          console.error('Error fetching SVG:', error)
        } finally {
          setSvgLoading(false)
        }
      }

      fetchSvg()
    }
  }, [imgSrc, isSvg])

  // ===== Source strategy =====
  useEffect(() => {
    // Reset per-file state
    setAnimateTransforms(false)
    setUseCssFit(true)
    setZoomFactor(1)
    setPan({ x: 0, y: 0 })
    autoFitRef.current = true

    // cleanup previous request + URL if any
    fetchCtrlRef.current?.abort()
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl)
      setObjectUrl(null)
    }

    if (!isAvailable || !fileData?.url) {
      setImgSrc(null)

      return
    }

    // Try direct streaming URL first for earliest paint
    setImgSrc(fileData.url)

    return () => {
      fetchCtrlRef.current?.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailable, fileData?.url])

  // revoke object URL on unmount
  useEffect(() => {
    return () => {
      fetchCtrlRef.current?.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ===== Controls (zoom/pan actions) - only for images =====
  const handleZoomIn = useCallback(() => {
    if (!isImage) return
    autoFitRef.current = false
    if (useCssFit) {
      computeBaseScale()
      setUseCssFit(false)
      requestAnimationFrame(() => setAnimateTransforms(true))
    }
    setZoomFactor(z => clamp(z * 1.2, 0.1, 5))
  }, [useCssFit, computeBaseScale, isImage])

  const handleZoomOut = useCallback(() => {
    if (!isImage) return
    autoFitRef.current = false
    if (useCssFit) {
      computeBaseScale()
      setUseCssFit(false)
      requestAnimationFrame(() => setAnimateTransforms(true))
    }
    setZoomFactor(z => {
      const next = clamp(z / 1.2, 0.1, 5)

      if (next <= 1) setPan({ x: 0, y: 0 })

      return next
    })
  }, [useCssFit, computeBaseScale, isImage])

  const handleReset = useCallback(() => {
    if (!isImage) return
    autoFitRef.current = true
    if (useCssFit) {
      setPan({ x: 0, y: 0 })
      setZoomFactor(1)

      return
    }
    setZoomFactor(1)
    setPan({ x: 0, y: 0 })
    fitToContainer()
  }, [useCssFit, fitToContainer, isImage])

  // Auto-download when URL becomes available after processing
  useEffect(() => {
    if (pendingDownload && fileData?.url && fileData?.name) {
      setPendingDownload(false)
      const url = fileData.url
      const name = fileData.name

      try {
        const a = document.createElement('a')

        a.href = url
        a.download = name
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch {
        // Fallback: try opening in new window if download attribute doesn't work
        window.open(url, '_blank')
      }
    }
  }, [pendingDownload, fileData?.url, fileData?.name])

  const handleDownload = useCallback(async () => {
    if (!fileData?.name || !fileData?.fileId) return

    // If URL is not available, request download first
    let url = fileData.url

    if (!url) {
      setIsRequestingDownload(true)
      try {
        const downloadResponse = await requestDownload({ id: fileData.fileId })

        if (downloadResponse.status === 'AVAILABLE' && downloadResponse.url) {
          // URL is immediately available, download now
          url = downloadResponse.url
          // Update modal data with the URL but keep the original status
          // (don't change NO_PREVIEW to AVAILABLE since file still isn't previewable)
          setData({ ...fileData, url })
          setIsRequestingDownload(false)

          // Download immediately
          try {
            const a = document.createElement('a')

            a.href = url
            a.download = fileData.name
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          } catch {
            window.open(url, '_blank')
          }
        } else if (downloadResponse.status === 'PROCESSING') {
          // File is being decrypted, wait for FILE_READY_FOR_DOWNLOAD event
          // Keep the original status (NO_PREVIEW) and just mark that we're waiting for download
          setPendingDownload(true)
          setIsRequestingDownload(false)
          // Note: We don't change the status here because NO_PREVIEW should remain visible
          // The WebSocket event handler will update the URL when ready
          // and our useEffect will trigger the download
        } else {
          // Other status or no URL
          setIsRequestingDownload(false)

          return
        }
      } catch {
        setIsRequestingDownload(false)

        return
      }
    } else {
      // URL is already available, download immediately
      const name = fileData.name

      try {
        const a = document.createElement('a')

        a.href = url
        a.download = name
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch {
        // Fallback: try opening in new window if download attribute doesn't work
        window.open(url, '_blank')
      }
    }
  }, [fileData, setData])

  const handleFavorite = useCallback(async () => {
    if (!fileData?.fileId) return
    try {
      await bulkToggleFavorite.mutateAsync({ fileIds: [fileData.fileId] })
      // change modal state to the opposite of current state
      // update fileData with the new favorite status
      setData({ ...data, isFavorite: !fileData.isFavorite })
    } catch (e) {
      console.error('Toggle favorite failed:', e)
    }
  }, [fileData.fileId, fileData.isFavorite, bulkToggleFavorite, setData, data])

  const handleClose = useCallback(() => {
    try {
      fetchCtrlRef.current?.abort()
      fetchCtrlRef.current = null
      if (imgRef.current && imgRef.current.src) {
        setTimeout(() => {
          if (imgRef.current) {
            imgRef.current.src = ''
          }
        }, 1000)
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
        setObjectUrl(null)
      }
    } finally {
      onClose()
    }
  }, [objectUrl, onClose])

  const onBackdropPointerDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Check if clicking on the backdrop (not on content)
      const target = e.target as HTMLElement
      const contentElement = contentRef.current

      if (target === e.currentTarget) {
        handleClose()

        return
      }
      if (target === contentElement) {
        handleClose()

        return
      }
      if (
        target.closest('video') ||
        target.closest('img') ||
        target.closest('svg') ||
        target.closest('[role="button"]') ||
        target.closest('input') ||
        target.closest('button') ||
        target.closest('.preview-content') ||
        target.closest('.bottom-controls') ||
        target.tagName === 'VIDEO' ||
        target.tagName === 'IMG' ||
        target.tagName === 'SVG' ||
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'PRE'
      ) {
        return
      }
      handleClose()
    },
    [handleClose],
  )

  const effectiveScale = baseScale * zoomFactor

  // Ensure cached object URLs or cached network images still trigger load init
  useLayoutEffect(() => {
    const img = imgRef.current

    if (!img || !isImage) return
    if (img.complete && img.naturalWidth > 0) {
      onImageLoad()
    }
  }, [imgSrc, onImageLoad, isImage])

  const handleDoubleClickImage = () => {
    const img = imgRef.current
    const container = contentRef.current

    if (!img || !container) return

    if (useCssFit) {
      computeBaseScale()
      setUseCssFit(false)
    }

    if (Math.abs(zoomFactor - 1) < 0.001) {
      const toActual = clamp(1 / baseScale, 0.1, 5)

      autoFitRef.current = false
      setZoomFactor(toActual)
    } else {
      setZoomFactor(1)
      setPan({ x: 0, y: 0 })
      autoFitRef.current = true
    }
    requestAnimationFrame(() => setAnimateTransforms(true))
  }

  return (
    <div
      aria-label={`Preview: ${fileData.name}`}
      aria-modal="true"
      role="dialog"
      className="
        fixed inset-0 z-[1000]
        flex flex-col
        overscroll-none
        bg-black/60 backdrop-blur-[2px]
        dark:bg-black/70
      "
    >
      {/* HEADER */}
      <div className="relative h-14 flex items-center px-3 md:px-4 select-none">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/50 to-transparent" />
        <div className="relative z-10 flex w-full items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-3">
            <Tooltip content="Close (Esc)" placement="bottom">
              <Button
                isIconOnly
                aria-label="Close"
                className="bg-white/15 hover:bg-white/25 text-white min-w-4 rounded-full flex-shrink-0"
                size="sm"
                variant="flat"
                onPress={handleClose}
              >
                <X className="h-5 w-5" />
              </Button>
            </Tooltip>
            {/* File type icon */}

            <FileTypeIcon
              filename={fileData.name}
              mediaType={fileData.mediaType}
              mimeType={fileData.mimeType}
              size={30}
            />

            <h1
              className="truncate text-sm md:text-base font-medium text-white"
              title={fileData.name}
            >
              {fileData.name}
            </h1>
          </div>
          {!isProcessing && !hasError && (
            <div className="flex items-center gap-1 md:gap-2">
              <Tooltip content="Favorite" placement="bottom">
                <Button
                  aria-label="Toggle favorite"
                  className="text-white/90 hover:bg-white/10 min-w-4"
                  disabled={bulkToggleFavorite.isPending}
                  isLoading={bulkToggleFavorite.isPending}
                  size="sm"
                  variant="light"
                  onPress={handleFavorite}
                >
                  <Star
                    className={`sm:mr-1 h-4 w-4 ${fileData.isFavorite ? 'text-yellow-400' : ''}`}
                    fill={fileData.isFavorite ? 'currentColor' : 'none'}
                  />
                  <span className="hidden sm:inline">Favorite</span>
                </Button>
              </Tooltip>
              <Tooltip
                content={isDownloading ? 'Preparing download...' : 'Download'}
                placement="bottom"
              >
                <Button
                  aria-label="Download"
                  className="text-white/90 hover:bg-white/10 min-w-4"
                  disabled={isDownloading}
                  isLoading={isDownloading}
                  size="sm"
                  variant="light"
                  onPress={handleDownload}
                >
                  <Download className="mr-1 h-4 w-4" />
                  <span className="hidden sm:inline">Download</span>
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      {/* CONTENT */}
      <div
        ref={contentRef}
        className="
          relative flex-1 overflow-hidden
          bg-transparent
          flex items-center justify-center
        "
        onMouseDown={onBackdropPointerDown}
      >
        {/* LOADING/STATUS */}
        {isProcessing && (
          <div aria-live="polite" className="text-center">
            <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-b-2 border-neutral-300" />
            <p className="text-base font-medium text-neutral-100">
              {fileData.status === 'DECRYPTING' ? 'Decrypting file...' : 'Processing file...'}
            </p>
            <p className="mt-1 text-sm text-neutral-300">Please wait while we prepare your file</p>
          </div>
        )}

        {hasError && (
          <div className="max-w-xl text-center text-white">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 text-red-400">
              <svg fill="none" height="28" viewBox="0 0 24 24" width="28">
                <path
                  d="M12 9v4m0 4h.01M4.5 19h15a1.5 1.5 0 0 0 1.3-2.25L13.3 4.75a1.5 1.5 0 0 0-2.6 0L3.2 16.75A1.5 1.5 0 0 0 4.5 19Z"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-semibold">Unable to preview this file</h3>
            <p className="text-neutral-200">
              {fileData.errorMessage ||
                'Unable to preview this file. The file may be corrupted or temporarily unavailable. Please try again or download the file directly.'}
            </p>
          </div>
        )}

        {hasNoPreview && (
          <h3 className=" max-w-2xl text-center text-white preview-content text-xl font-semibold">
            No preview available
          </h3>
        )}

        {/* VIDEO */}
        {isAvailable && !hasError && !isProcessing && isVideo && fileData.url && (
          <VideoPlayer src={fileData.url} />
        )}

        {/* PDF */}
        {isAvailable && !hasError && !isProcessing && isPdf && fileData.url && (
          <PDFPreview url={fileData.url} />
        )}

        {/* TEXT */}
        {isAvailable && !hasError && !isProcessing && isText && fileData.url && (
          <TextPreview url={fileData.url} />
        )}

        {/* CSV */}
        {isAvailable && !hasError && !isProcessing && isCSV && fileData.url && (
          <CSVPreview url={fileData.url} />
        )}

        {/* IMAGE */}
        {svgError && (
          <div className="absolute inset-0 grid place-items-center text-red-500">
            <p>Failed to load SVG. Please try again later.</p>
          </div>
        )}
        {isAvailable && !hasError && !isProcessing && isImage && (
          <div className="relative w-full h-full flex items-center justify-center p-8">
            <div className="relative w-full h-full flex items-center justify-center">
              <div
                role="presentation"
                style={{ transform: `translate(${pan.x}px, ${pan.y}px)` }}
                className={`
                  relative select-none
                  ${zoomFactor > 1 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'}
                `}
                onDoubleClick={handleDoubleClickImage}
                onMouseDown={onMouseDownImage}
              >
                {svgContent && isSvg && (
                  <div className="max-w-full max-h-full object-contain">
                    {svgLoading ? (
                      <div className="absolute inset-0 grid place-items-center">
                        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-transparent" />
                      </div>
                    ) : svgContent ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: svgContent ?? '' }}
                        className={[
                          useCssFit
                            ? 'max-w-full max-h-full object-contain'
                            : 'max-w-none object-contain',
                          !useCssFit && animateTransforms
                            ? 'transition-transform duration-150'
                            : '',
                          'will-change-transform',
                        ].join(' ')}
                        style={
                          useCssFit
                            ? undefined
                            : {
                                transform: `scale(${effectiveScale})`,
                                imageRendering: 'auto',
                              }
                        }
                      />
                    ) : null}
                  </div>
                )}
                {imgSrc && !isSvg && (
                  <img
                    ref={imgRef}
                    alt={fileData.name}
                    crossOrigin="anonymous"
                    draggable={false}
                    fetchPriority="high"
                    loading="eager"
                    src={imgSrc ?? undefined}
                    className={[
                      useCssFit
                        ? 'max-w-full max-h-full object-contain'
                        : 'max-w-none object-contain',
                      !useCssFit && animateTransforms ? 'transition-transform duration-150' : '',
                      'will-change-transform',
                    ].join(' ')}
                    style={
                      useCssFit
                        ? undefined
                        : {
                            transform: `scale(${effectiveScale})`,
                            imageRendering: 'auto',
                          }
                    }
                    onLoad={onImageLoad}
                  />
                )}
              </div>

              {/* Bottom-center zoom pill - only for images (PDF and text have their own controls) */}
              {isImage && (
                <div className="pointer-events-none absolute inset-x-0 bottom-[max(16px,env(safe-area-inset-bottom))] flex justify-center bottom-controls">
                  <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-white/15 bg-black/60 px-2 py-1 backdrop-blur-md shadow-lg">
                    <Tooltip content="Zoom out" placement="top">
                      <Button
                        isIconOnly
                        aria-label="Zoom out"
                        className="text-white/90 hover:bg-white/10"
                        size="sm"
                        variant="light"
                        onPress={handleZoomOut}
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                    </Tooltip>

                    <span className="mx-1 w-12 text-center text-xs text-white/90 tabular-nums">
                      {Math.round(zoomFactor * 100)}%
                    </span>

                    <Tooltip content="Zoom in" placement="top">
                      <Button
                        isIconOnly
                        aria-label="Zoom in"
                        className="text-white/90 hover:bg-white/10"
                        size="sm"
                        variant="light"
                        onPress={handleZoomIn}
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                    </Tooltip>

                    <div className="mx-1 h-4 w-px bg-white/20" />

                    <Tooltip content="Reset to fit" placement="top">
                      <Button
                        isIconOnly
                        aria-label="Reset to fit"
                        className="text-white/90 hover:bg-white/10"
                        size="sm"
                        variant="light"
                        onPress={handleReset}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
